/**
 * Semantic layer, Agents, Trust & governance, Decision log
 * Restored - these explain how an answer is built and why it can be trusted.
 * Demo content - see src/demo/data.ts.
 */

import React from 'react';
import { AppShell } from '../../components/shell/AppShell';
import { Card, Kpi, PageHead, Note, Table, Tag, DemoBadge } from '../../components/ui';
import {
  METRIC_CONFLICTS,
  CERTIFIED_METRICS,
  PIPELINE_STEPS,
  AGENT_ROSTER,
  AGENT_ACTIVITY,
  TRUST_RULES,
  ROLE_SCOPES,
  AUDIT_LOG,
  DECISION_LOG,
} from '../../demo/data';

/* --------------------------------------------------- semantic layer */

export const SemanticLayerPage: React.FC = () => (
  <AppShell
    title="Semantic layer"
    sub="Certified definitions"
    actions={
      <>
        <DemoBadge />
        <button className="btn btn-g">Lineage</button>
        <button className="btn btn-p">Certify a metric</button>
      </>
    }
  >
    <PageHead
      eyebrow="Semantic layer"
      title="One definition of every number"
      lede="Most wrong AI answers are not hallucinations - they are the right query against the wrong definition. Revenue in Salesforce is pipeline; in SAP it is invoiced. OmniMind resolves every business term through a certified definition before it touches a source."
    />

    <div className="g g4" style={{ marginBottom: 12 }}>
      <Kpi label="Certified metrics" value="64" color="var(--green)" icon="check" />
      <Kpi label="Conflicts detected" value="11" color="var(--red)" valueColor="var(--red)" icon="warn" sub="same term, different logic" />
      <Kpi label="Awaiting certification" value="7" color="var(--amber)" icon="clock" />
      <Kpi label="Answer accuracy" value="95" unit="%" color="var(--teal)" icon="target" pip="from 75% uncertified" dir="up" />
    </div>

    <div className="g g3" style={{ marginBottom: 12 }}>
      <div className="s2">
        <Card title="Where your systems disagree" sub="11 conflicts · 6 shown" flush>
          <Table
            cols={[
              { t: 'Term' },
              { t: 'Salesforce' },
              { t: 'SAP' },
              { t: 'Odoo' },
              { t: 'Excel' },
              { t: 'Certified as' },
              { t: 'Owner' },
            ]}
            rows={METRIC_CONFLICTS.map((c) => [
              <b>{c.term}</b>,
              c.sf,
              c.sap,
              c.odoo,
              c.excel,
              <Tag tone={c.tone as any}>{c.certified}</Tag>,
              c.owner,
            ])}
          />
        </Card>
      </div>

      <Card title="Why this matters">
        <p style={{ fontSize: 12.5, lineHeight: 1.65, color: '#2A3338' }}>
          Finance reports gross margin at 38%, operations at 41%, and sales has a third number. Each
          is technically correct. None is definitive.
        </p>
        <p style={{ fontSize: 12.5, lineHeight: 1.65, color: '#2A3338', marginTop: 8 }}>
          An engine that answers from whichever table it finds first will produce all three -
          confidently, and faster than any analyst. So OmniMind refuses to answer a question about an{' '}
          <b>uncertified</b> metric without saying so.
        </p>
        <Note style={{ marginTop: 10 }}>
          Adding certified definitions and business context is the single largest accuracy lever -
          larger than the model.
        </Note>
      </Card>
    </div>

    <Card title="Certified definitions" sub="versioned, owned, auditable" flush>
      <Table
        cols={[
          { t: 'ID' },
          { t: 'Metric' },
          { t: 'Definition' },
          { t: 'Authoritative source' },
          { t: 'Grain' },
          { t: 'Certified by' },
          { t: 'Version' },
        ]}
        rows={CERTIFIED_METRICS.map((m) => [
          <span className="mono dim">{m.id}</span>,
          <b>{m.name}</b>,
          <span className="dim">{m.definition}</span>,
          m.source,
          m.grain,
          m.by,
          <Tag>{m.version}</Tag>,
        ])}
      />
    </Card>
  </AppShell>
);

/* ------------------------------------------------------------ agents */

export const AgentsPage: React.FC = () => (
  <AppShell
    title="Agents"
    sub="How answers are built"
    actions={
      <>
        <DemoBadge />
        <button className="btn btn-g">Run history</button>
      </>
    }
  >
    <PageHead
      eyebrow="Agents"
      title="The workers behind every answer"
      lede="Each source has an agent that knows its schema, its quirks and its rate limits. When you ask a question, a planner decides which agents to call, they run in parallel, and a reconciler checks their answers against each other before anything reaches you."
    />

    <div className="g g5" style={{ marginBottom: 12 }}>
      <Kpi label="Agents" value="16" color="var(--blue)" icon="spark" />
      <Kpi label="Queries today" value="1,284" color="var(--purple)" icon="db" />
      <Kpi label="Median answer time" value="4.2" unit="s" color="var(--green)" icon="clock" />
      <Kpi label="Reconciliation conflicts" value="23" color="var(--amber)" icon="warn" sub="surfaced, not hidden" />
      <Kpi label="Writes executed" value="0" color="var(--muted-2)" icon="lock" sub="read-only" />
    </div>

    <div className="g g3" style={{ marginBottom: 12 }}>
      <div className="s2">
        <Card title="How a question becomes an answer">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {PIPELINE_STEPS.map((s) => (
              <div key={s.n} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 9,
                    background: s.color,
                    color: '#fff',
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 11,
                    fontWeight: 800,
                    flex: 'none',
                  }}
                >
                  {s.n}
                </span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 800 }}>{s.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5, marginTop: 2 }}>
                    {s.body}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card title="Agent roster">
        {AGENT_ROSTER.map((a) => (
          <div className="split" key={a.name}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: a.color }} />
              <b>{a.name}</b>
            </span>
            <span className="dim" style={{ fontSize: 11 }}>
              {a.role}
            </span>
          </div>
        ))}
      </Card>
    </div>

    <Card title="Recent agent activity" flush>
      <Table
        cols={[
          { t: 'Time' },
          { t: 'Question' },
          { t: 'Agents' },
          { t: 'Rows read', num: true },
          { t: 'Conflicts', num: true },
          { t: 'Confidence', num: true },
          { t: 'User' },
        ]}
        rows={AGENT_ACTIVITY.map((a) => [
          <span className="mono dim">{a.time}</span>,
          <b>{a.question}</b>,
          <span className="dim">{a.agents}</span>,
          a.rows,
          a.conflicts === '0' ? '0' : <Tag tone="a">{a.conflicts}</Tag>,
          a.confidence,
          a.user,
        ])}
      />
    </Card>
  </AppShell>
);

/* ------------------------------------------------------------- trust */

export const TrustPage: React.FC = () => (
  <AppShell title="Trust & governance" sub="Permissions, citations, audit" actions={<DemoBadge />}>
    <PageHead
      eyebrow="Trust & governance"
      title="What stops a clever answer being a wrong one"
      lede="Leaders will only act on an answer they can check. Everything here exists so that any number OmniMind gives you can be traced to the record it came from, by you or by an auditor."
    />

    <div className="g g5" style={{ marginBottom: 12 }}>
      <Kpi label="Answers with citations" value="100" unit="%" color="var(--green)" icon="check" />
      <Kpi label="Permission checks" value="1,284" color="var(--blue)" icon="lock" sub="one per question" />
      <Kpi label="Declined — no access" value="14" color="var(--amber)" icon="warn" sub="user lacked rights" />
      <Kpi label="Declined — no evidence" value="9" color="var(--purple)" icon="warn" sub="refused to guess" />
      <Kpi label="Data leaving region" value="0" color="var(--green)" icon="lock" />
    </div>

    <div className="g g2" style={{ marginBottom: 12 }}>
      <Card title="The rules">
        {TRUST_RULES.map(([title, body], i) => (
          <div className="dreq" key={title}>
            <span className="st2" style={{ background: 'var(--green-s)', color: '#0A7A55' }}>
              {i + 1}
            </span>
            <div>
              <div style={{ fontWeight: 700 }}>{title}</div>
              <div className="t2">{body}</div>
            </div>
          </div>
        ))}
      </Card>

      <Card title="Who can ask what" sub="inherited from source systems, not configured twice" flush>
        <Table
          cols={[{ t: 'Role' }, { t: 'Scope' }, { t: 'Finance detail' }, { t: 'People data' }, { t: 'Board docs' }]}
          rows={ROLE_SCOPES.map((r) => [
            r.role,
            r.scope,
            <Tag tone={r.finance[1] as any}>{r.finance[0]}</Tag>,
            <Tag tone={r.people[1] as any}>{r.people[0]}</Tag>,
            <Tag tone={r.board[1] as any}>{r.board[0]}</Tag>,
          ])}
        />
      </Card>
    </div>

    <Card title="Audit log" flush>
      <Table
        cols={[{ t: 'Time' }, { t: 'User' }, { t: 'Question' }, { t: 'Sources read' }, { t: 'Outcome' }]}
        rows={AUDIT_LOG.map((a) => [
          <span className="mono dim">{a.time}</span>,
          <b>{a.user}</b>,
          a.question,
          <span className="dim">{a.sources}</span>,
          <Tag tone={a.tone as any}>{a.outcome}</Tag>,
        ])}
      />
    </Card>
  </AppShell>
);

/* ------------------------------------------------------ decision log */

export const DecisionLogPage: React.FC = () => (
  <AppShell title="Decision log" sub="Evidence and outcomes" actions={<DemoBadge />}>
    <PageHead
      eyebrow="Decision log"
      title="What was decided, on what evidence, and how it turned out"
      lede="Every decision taken through OmniMind is kept with the data it rested on and its readiness score at the time. Six months later you can see which decisions the evidence supported - and whether instinct or data called it better."
    />

    <div className="g g4" style={{ marginBottom: 12 }}>
      <Kpi label="Decisions logged" value="38" color="var(--blue)" icon="check" />
      <Kpi label="Median readiness" value="78" unit="%" color="var(--green)" icon="target" />
      <Kpi label="Outcome tracked" value="29" color="var(--purple)" icon="chart" />
      <Kpi label="Decided below 60%" value="6" color="var(--amber)" icon="warn" sub="4 of them reversed" />
    </div>

    <Card title="History" flush>
      <Table
        cols={[
          { t: 'Decision' },
          { t: 'Date' },
          { t: 'Readiness' },
          { t: 'Choice' },
          { t: 'Outcome' },
          { t: 'Verdict' },
        ]}
        rows={DECISION_LOG.map((d) => [
          <b>{d.name}</b>,
          d.date,
          <span className="mono">{d.readiness}</span>,
          d.choice,
          <span className="dim">{d.outcome}</span>,
          <Tag tone={d.tone as any}>{d.verdict}</Tag>,
        ])}
      />
    </Card>

    <Note style={{ marginTop: 12 }}>
      <b>Pattern worth noticing:</b> of the six decisions taken below 60% readiness, four were later
      reversed or partly reversed. None of the decisions taken above 80% were. That is the case for
      waiting for the missing inputs on Riyadh.
    </Note>
  </AppShell>
);
